import React, { useState } from 'react';
import { useDropzone } from 'react-dropzone';
import Papa from 'papaparse';
import { useProfileStore } from '../../state/profileStore';

function UploadModal({ onClose, onUploadComplete }) {
  const user = useProfileStore((s) => s.profile);
  const [uploadStatus, setUploadStatus] = useState(null);

const handleUpload = async (rows) => {
  try {
    // Normalize each row regardless of header order
    const normalizedRows = rows.map(row => ({
      customer_firstname: row.customer_firstname || '',
      customer_lastname: row.customer_lastname || '',
      customer_email: row.customer_email || '',
      customer_phone: row.customer_phone || '',
      customer_zipcode: row.customer_zipcode || ''
    }));

    // Filter valid rows with emails
    const validRows = normalizedRows.filter(row => 
      row.customer_email && row.customer_email.trim() !== ''
    );

    if (validRows.length === 0) {
      throw new Error('No valid records with email addresses found');
    }

    // Check for duplicates
    const duplicateResponse = await fetch('/api/customer-cards/check-duplicates', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({
        user_id: user.u_id,
        emails: validRows.map(row => row.customer_email)
      })
    });

    if (!duplicateResponse.ok) throw new Error('Duplicate check failed');
    
    const { duplicates } = await duplicateResponse.json();
    
    // Filter out duplicates
    const uniqueRows = validRows.filter(
      row => !duplicates.includes(row.customer_email.toLowerCase())
    );

    if (uniqueRows.length === 0) {
      throw new Error('All records are duplicates');
    }

    // Upload only unique records
    const uploadResponse = await fetch('/api/customer-cards/add-customers-bulk', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      credentials: 'include',
      body: JSON.stringify({ customers: uniqueRows }),
    });

    if (!uploadResponse.ok) throw new Error('Upload failed');
    
    setUploadStatus({
      status: 'success',
      stats: {
        total: validRows.length,
        duplicates: duplicates.length,
        uploaded: uniqueRows.length
      }
    });
    
    return { 
      success: true,
      stats: {
        total: validRows.length,
        duplicates: duplicates.length,
        uploaded: uniqueRows.length
      }
    };

  } catch (error) {
    console.error('Upload failed:', error);
    setUploadStatus({
      status: 'error',
      error: error.message
    });
    return { 
      success: false,
      error: error.message
    };
  }
};

  const { getRootProps, getInputProps } = useDropzone({
    accept: '.csv',
    maxFiles: 1,
    onDrop: acceptedFiles => {
      setUploadStatus(null); // Reset status on new file drop
      Papa.parse(acceptedFiles[0], {
        header: true,
        complete: (results) => {
          handleUpload(results.data)
            .then(result => {
              if (result?.success && onUploadComplete) {
                onUploadComplete(result);
              }
            });
        }
      });
    }
  });

  return (
    <div className="fixed inset-0 bg-black bg-opacity-40 flex items-center justify-center z-50">
      <div className="bg-white rounded-lg shadow-lg w-full max-w-md p-6 mx-4">
        <h2 className="text-2xl font-bold text-center text-[#00aef7] mb-4">Upload Your Customer Information</h2>
        
        {/* Success Message */}
        {uploadStatus?.status === 'success' && (
          <div className="mb-4 p-3 bg-green-100 border border-green-400 text-green-700 rounded">
            <p className="font-bold">✓ Upload successful!</p>
            <p>Records processed: {uploadStatus.stats.total}</p>
            <p>Duplicates skipped: {uploadStatus.stats.duplicates}</p>
            <p>New records added: {uploadStatus.stats.uploaded}</p>
          </div>
        )}

        {/* Error Message */}
        {uploadStatus?.status === 'error' && (
          <div className="mb-4 p-3 bg-red-100 border border-red-400 text-red-700 rounded">
            {uploadStatus.error.includes('duplicates') 
              ? 'All records were duplicates - nothing was uploaded'
              : `Upload failed: ${uploadStatus.error}`}
          </div>
        )}

        <div 
          {...getRootProps()}
          className="border-2 border-dashed border-gray-300 rounded-lg p-6 text-center cursor-pointer mb-4 hover:bg-gray-50 transition-colors"
        >
          <input {...getInputProps()} />
          <p className="text-gray-600 mb-1">
            Drag & drop a CSV file here, or click to select
          </p>
          <p className="text-sm text-gray-500">
            (25 records max per upload)
          </p>
        </div>

        <div className="flex justify-between items-center">
          <button
            onClick={onClose}
            className="px-4 py-2 rounded bg-gray-200 text-gray-800 hover:bg-gray-300 transition"
          >
            Close
          </button>
          <a
            href="/sample-customer-upload.csv"
            download
            className="px-4 py-2 rounded bg-[#00aef7] text-white hover:bg-[#008fc5] transition text-center"
          >
            Download Sample CSV
          </a>
        </div>
      </div>
    </div>
  );
}

export default UploadModal;